import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import api, { endpoints } from '../utils/apiClient';

// Define admin addresses that should have admin access
export const ADMIN_ADDRESSES = [
  // Add admin wallet addresses here (in lowercase)
];

const ProtectedAdminRoute = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const checkPerformed = useRef(false);
  const refreshingToken = useRef(false);

  // Function to refresh the admin token
  const refreshAdminToken = async () => {
    try {
      if (refreshingToken.current) return false;
      refreshingToken.current = true;

      console.log('Attempting to refresh admin token...');

      // Get the current token
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found to refresh');
        refreshingToken.current = false;
        return false;
      }

      try {
        // Make a request to the token verification endpoint
        console.log('Making verification request with token:', token.substring(0, 10) + '...');
        const response = await api.get(endpoints.auth.verifyToken);

        // If verification is successful, we're still logged in
        if (response && response.valid && response.user && response.user.isAdmin) {
          console.log('Token verified successfully');

          // Update the user in localStorage with the latest data
          const userStr = localStorage.getItem('user');
          if (userStr) {
            try {
              const userData = JSON.parse(userStr);
              const updatedUser = {
                ...userData,
                ...response.user,
                isAdmin: true,
                // Use the existing token since it's already working
                token: token,
                // Refresh the session expiration time for another 4 hours
                sessionExpiresAt: Date.now() + (4 * 60 * 60 * 1000)
              };
              localStorage.setItem('user', JSON.stringify(updatedUser));
            } catch (e) {
              console.error('Error updating user data', e);
            }
          }

          refreshingToken.current = false;
          return true;
        }

        console.log('Token verification failed: Not an admin user or invalid response');
        refreshingToken.current = false;
        return false;
      } catch (verifyError) {
        console.error('Token verification request failed:', verifyError);

        // If verification fails, try to get a fresh token using /api/auth/refresh
        try {
          console.log('Attempting to get a fresh token via refresh endpoint');
          const refreshResponse = await api.get(endpoints.auth.refreshToken);

          if (refreshResponse && refreshResponse.token) {
            console.log('Successfully refreshed token');

            // Store the new token - ensure it's clean (no Bearer prefix)
            const newToken = refreshResponse.token.startsWith('Bearer ')
              ? refreshResponse.token.substring(7).trim()
              : refreshResponse.token.trim();

            localStorage.setItem('token', newToken);

            // Update user data with new token
            const userStr = localStorage.getItem('user');
            if (userStr && refreshResponse.user) {
              try {
                const userData = JSON.parse(userStr);
                const updatedUser = {
                  ...userData,
                  ...refreshResponse.user,
                  token: newToken,
                  isAdmin: true,
                  // Refresh the session expiration time for another 4 hours
                  sessionExpiresAt: Date.now() + (4 * 60 * 60 * 1000)
                };
                localStorage.setItem('user', JSON.stringify(updatedUser));
              } catch (e) {
                console.error('Error updating user data with new token', e);
              }
            }

            refreshingToken.current = false;
            return true;
          }
        } catch (refreshError) {
          console.error('Token refresh attempt failed:', refreshError);
        }
      }

      console.log('All token refresh attempts failed');
      refreshingToken.current = false;
      return false;
    } catch (error) {
      console.error('Error in token refresh flow:', error);
      refreshingToken.current = false;
      return false;
    }
  };

  // Set up a periodic token refresh
  useEffect(() => {
    // Only set up the interval if we're logged in as admin
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!userStr || !token) return;

    try {
      const userData = JSON.parse(userStr);
      if (!userData.isAdmin) return;

      // Set up an interval to refresh the token every hour
      // This ensures the admin session stays active
      const intervalId = setInterval(() => {
        console.log('Running scheduled token refresh...');
        refreshAdminToken();
      }, 60 * 60 * 1000); // 1 hour

      return () => clearInterval(intervalId);
    } catch (error) {
      console.error('Error setting up token refresh interval:', error);
    }
  }, []);

  useEffect(() => {
    // Only run this check once to prevent infinite loops
    if (checkPerformed.current) return;

    const checkAdminStatus = async () => {
      // Check if we're in development mode
      if (import.meta.env.DEV) {
        console.log('Development mode detected, bypassing admin check in ProtectedAdminRoute');
        setIsAdmin(true);
        setLoading(false);
        checkPerformed.current = true;
        return;
      }

      // Check if we have any admin token
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      if (!token || !userStr) {
        console.log('No token or user data found');
        setLoading(false);
        // Use imperative navigation instead of declarative Navigate component
        if (checkPerformed.current === false) {
          checkPerformed.current = true;
          navigate('/admin-login', { replace: true });
        }
        return;
      }

      try {
        const localUser = JSON.parse(userStr);

        // Check if the token has expired
        if (localUser.expiresAt && Date.now() > localUser.expiresAt) {
          console.log('Admin token has expired at:', new Date(localUser.expiresAt).toLocaleString());
          // Clear expired data and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('isAdminSession');
          if (checkPerformed.current === false) {
            checkPerformed.current = true;
            toast.error('Your session has expired. Please log in again.');
            navigate('/admin-login', { replace: true });
          }
          return;
        }

        // Check if the session has expired but token is still valid
        // If so, refresh the session expiration time instead of logging out
        if (localUser.sessionExpiresAt && Date.now() > localUser.sessionExpiresAt) {
          console.log('Admin session has expired at:', new Date(localUser.sessionExpiresAt).toLocaleString());
          console.log('Refreshing session expiration time...');

          // Update the session expiration time for another 4 hours
          localUser.sessionExpiresAt = Date.now() + (4 * 60 * 60 * 1000);
          localStorage.setItem('user', JSON.stringify(localUser));

          console.log('Session refreshed, new expiration:', new Date(localUser.sessionExpiresAt).toLocaleString());
        }

        // Check admin rights
        const hasAdminRights = localUser?.isAdmin === true;

        if (!hasAdminRights) {
          console.log('User in localStorage does not have admin rights, checking with server...');
          // Try to refresh token to verify admin status with the server
          const refreshSuccessful = await refreshAdminToken();

          if (refreshSuccessful) {
            console.log('Token refresh confirmed admin status');
            setIsAdmin(true);
          } else {
            console.log('User does not have admin rights after refresh attempt');
            if (checkPerformed.current === false) {
              checkPerformed.current = true;
              navigate('/admin-login', { replace: true });
            }
            return;
          }
        } else {
          setIsAdmin(true);
        }
      } catch (err) {
        console.error("Error parsing user data", err);
        if (checkPerformed.current === false) {
          checkPerformed.current = true;
          navigate('/admin-login', { replace: true });
        }
      } finally {
        setLoading(false);
        checkPerformed.current = true;
      }
    };

    checkAdminStatus();
  }, [user, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
    </div>;
  }

  // We don't need the Navigate component here anymore since we use
  // imperative navigation inside useEffect
  if (!isAdmin) {
    return null;
  }

  return children;
};

export default ProtectedAdminRoute;
